import { queryOptions } from '@tanstack/react-query';

import { api } from '@lib/api/client';
import type {
  EnhancedStoreCart,
  EnhancedStorePromotion,
  EnrichedLineItem,
} from '@marxbiotech/shared-types/medusa';

// =============================================================================
// Cart API Response Types
// =============================================================================

/**
 * Response from GET /carts/current
 */
interface CartResponse {
  cart: EnhancedStoreCart | null;
}

/**
 * Response from GET /carts/current/data
 */
interface CartDataResponse {
  cart: EnhancedStoreCart | null;
  enriched_items: EnrichedLineItem[];
  checkout_threshold_amount: number;
  free_shipping_promotion: EnhancedStorePromotion | null;
}

/**
 * Response from GET /carts/current/quantity
 */
interface CartQuantityResponse {
  quantity: number;
}

// =============================================================================
// Cache Configuration
// =============================================================================

/**
 * Shared cache configuration for cart queries.
 * - staleTime: Data considered fresh for 30 seconds
 * - gcTime: Unused data garbage collected after 5 minutes
 */
const CART_CACHE_CONFIG = {
  staleTime: 30 * 1000, // 30 seconds
  gcTime: 5 * 60 * 1000, // 5 minutes
} as const;

// =============================================================================
// Query Keys
// =============================================================================

export const cartKeys = {
  all: ['cart'] as const,
  current: () => [...cartKeys.all, 'current'] as const,
  data: () => [...cartKeys.all, 'data'] as const,
  quantity: () => [...cartKeys.all, 'quantity'] as const,
};

// =============================================================================
// Query Options
// =============================================================================

/**
 * Query options for fetching cart.
 * Cart ID is read from _medusa_cart_id httpOnly cookie by BFF.
 */
export const cartQueryOptions = () =>
  queryOptions({
    queryKey: cartKeys.current(),
    queryFn: async () => {
      const response = await api.get<CartResponse>('/carts/current');
      return response.data.cart;
    },
    ...CART_CACHE_CONFIG,
  });

/**
 * Query options for fetching cart with enriched items.
 * Used for Cart Drawer and Cart page.
 */
export const cartDataQueryOptions = () =>
  queryOptions({
    queryKey: cartKeys.data(),
    queryFn: async () => {
      const response = await api.get<CartDataResponse>('/carts/current/data');
      return response.data;
    },
    ...CART_CACHE_CONFIG,
  });

/**
 * Query options for fetching cart quantity.
 * Used for Cart Icon badge.
 */
export const cartQuantityQueryOptions = () =>
  queryOptions({
    queryKey: cartKeys.quantity(),
    queryFn: async () => {
      const response =
        await api.get<CartQuantityResponse>('/carts/current/quantity');
      return response.data.quantity;
    },
    ...CART_CACHE_CONFIG,
  });
